import { lineString, featureCollection } from '@turf/helpers';
import flatten from '@turf/flatten';
import { events, modes } from './utils/constants';
import lineIntersect from '@turf/line-intersect';
import booleanDisjoint from '@turf/boolean-disjoint';
import lineOffset from '@turf/line-offset';
import lineToPolygon from '@turf/line-to-polygon';
import difference from '@turf/difference';
import { drawModes, drawState } from '../../Types';
import { GeoJSONFeature } from 'maplibre-gl';

const SplitPolygonMode = <drawModes>{};

type SplitPolygonState = drawState & {
  main: any[];
  sourceIds: string[];
  startedDraw?: boolean;
};

function isPolygonGeometry(type: unknown): boolean {
  return type === 'Polygon' || type === 'MultiPolygon';
}

function explodeToPolygons(geo: any): any[] {
  if (!geo) return [];
  const feature =
    geo.type === 'Feature'
      ? geo
      : { type: 'Feature', properties: {}, geometry: geo };
  const flat = flatten(featureCollection([feature]) as any);
  return flat.features.filter((f: any) => f.geometry?.type === 'Polygon');
}

SplitPolygonMode.onSetup = function () {
  const selected = this.getSelected();
  const main = selected
    .map((f: any) => {
      const gj = f.toGeoJSON?.() ?? f;
      const id = gj?.id ?? f?.id ?? gj?.properties?.id ?? f?.properties?.id;
      return {
        ...gj,
        id: id != null ? id : gj?.id,
      };
    })
    .filter((f: any) => isPolygonGeometry(f.geometry?.type));

  if (main.length < 1) {
    throw new Error(
      'Please select a polygon or multipolygon feature to split.',
    );
  }

  const sourceIds = main
    .map((f: any) => String(f.id ?? ''))
    .filter((id: string) => id && id !== 'undefined');

  // Prefer live draw selection ids — more reliable than toGeoJSON().id.
  const selectedIds = selected
    .map((f: any) => String(f?.id ?? ''))
    .filter((id: string) => id && id !== 'undefined');

  return {
    main,
    sourceIds: selectedIds.length > 0 ? selectedIds : sourceIds,
    startedDraw: false,
  } as SplitPolygonState;
};

SplitPolygonMode.toDisplayFeatures = function (
  state: SplitPolygonState,
  geojson: GeoJSONFeature,
  display: (feature: GeoJSONFeature) => void
) {
  display(geojson);

  if (state.startedDraw) return;
  state.startedDraw = true;

  this.changeMode('passing_mode_line_string', (cuttingLineString: any) => {
    (SplitPolygonMode as any).applyCut.call(this, state, cuttingLineString);
  });
};

(SplitPolygonMode as any).applyCut = function (state: SplitPolygonState, cuttingLineString: any) {
  const finishFailed = (reason: string) => {
    try {
      this.map.fire('draw.split.failed', { reason });
    } catch {
      /* ignore */
    }
    try {
      this.changeMode(modes.SIMPLE_SELECT, { featureIds: state.sourceIds });
    } catch {
      /* ignore */
    }
  };

  if (cuttingLineString?.geometry?.type !== 'LineString') {
    finishFailed('invalidCut');
    return;
  }

  const allPieces: any[] = [];
  const idsToDelete = new Set<string>();

  try {
    for (const el of state.main) {
      if (booleanDisjoint(el, cuttingLineString)) {
        finishFailed('lineMustCross');
        return;
      }

      const cutResult = polygonCut(el.geometry, cuttingLineString.geometry);

      const pieces = explodeToPolygons(cutResult);
      if (pieces.length < 2) {
        finishFailed('noSplit');
        return;
      }

      idsToDelete.add(String(el.id));
      for (const piece of pieces) {
        allPieces.push({
          type: 'Feature',
          properties: { ...(el.properties ?? {}) },
          geometry: piece.geometry,
        });
      }
    }
  } catch {
    finishFailed('invalidCut');
    return;
  }

  (SplitPolygonMode as any).commitSplit.call(this, state, [...idsToDelete], allPieces);
};

(SplitPolygonMode as any).commitSplit = function (
  state: SplitPolygonState,
  sourceIds: string[],
  pieces: any[]
) {
  const ids = [
    ...new Set(
      [...sourceIds, ...(state.sourceIds ?? [])]
        .map((id) => String(id ?? ''))
        .filter((id) => id && id !== 'undefined')
    ),
  ];

  for (const id of ids) {
    try {
      this.deleteFeature(String(id), { silent: true });
    } catch {
      /* ignore */
    }
  }

  // Belt-and-suspenders: drop any remaining selected source features by id match.
  try {
    for (const f of this.getAll?.()?.features ?? []) {
      const fid = String(f?.id ?? '');
      if (ids.includes(fid)) {
        this.deleteFeature(fid, { silent: true });
      }
    }
  } catch {
    /* ignore */
  }

  const created: any[] = [];
  const newIds: string[] = [];

  for (const piece of pieces) {
    const properties = { ...(piece.properties ?? {}) };
    delete properties.__tempFeatureId;
    delete properties.id;
    try {
      const newId = this._ctx.api.add({
        type: 'Feature',
        properties,
        geometry: piece.geometry,
      });
      if (newId != null) {
        newIds.push(String(newId));
        const added = this.getFeature(String(newId));
        if (added) created.push(added.toGeoJSON());
      }
    } catch {
      /* ignore single piece add failure */
    }
  }

  if (newIds.length < 2) {
    try {
      this.map.fire('draw.split.failed', { reason: 'invalidCut' });
    } catch {
      /* ignore */
    }
    try {
      this.changeMode(modes.SIMPLE_SELECT, { featureIds: state.sourceIds });
    } catch {
      /* ignore */
    }
    return;
  }

  try {
    this.map.fire(events.UPDATE, {
      action: 'SplitPolygon',
      features: created,
    });
    this.map.fire('draw.split', {
      count: newIds.length,
      featureIds: newIds,
    });
  } catch {
    /* ignore */
  }

  try {
    this.changeMode(modes.SIMPLE_SELECT, { featureIds: newIds });
  } catch {
    this.changeMode?.(modes.SIMPLE_SELECT);
  }
};

function polygonCut(poly: any, line: any): any {
  const THICK_LINE_UNITS = 'kilometers';
  const THICK_LINE_WIDTH = 0.000001;

  if (
    (poly.type !== 'Polygon' && poly.type !== 'MultiPolygon') ||
    line.type !== 'LineString'
  ) {
    return null;
  }

  const intersectPoints = lineIntersect(
    { type: 'Feature', properties: {}, geometry: poly },
    { type: 'Feature', properties: {}, geometry: line }
  );
  if (intersectPoints.features.length === 0) {
    return null;
  }

  if (booleanDisjoint(line, poly)) {
    return null;
  }

  const offsetLine = [
    lineOffset(line, THICK_LINE_WIDTH, { units: THICK_LINE_UNITS }),
    lineOffset(line, -THICK_LINE_WIDTH, { units: THICK_LINE_UNITS }),
  ];

  let clipped: any = null;

  for (let i = 0; i <= 1; i++) {
    const forCut = i;
    const polyCoords: number[][] = [];
    for (let j = 0; j < line.coordinates.length; j++) {
      polyCoords.push(line.coordinates[j] as number[]);
    }
    for (let j = offsetLine[forCut].geometry.coordinates.length - 1; j >= 0; j--) {
      polyCoords.push(offsetLine[forCut].geometry.coordinates[j] as number[]);
    }
    polyCoords.push(line.coordinates[0] as number[]);

    const thickLineString = lineString(polyCoords);
    const thickLinePolygon = lineToPolygon(thickLineString);
    clipped = difference(
      { type: 'Feature', properties: {}, geometry: poly },
      thickLinePolygon
    )?.geometry ?? null;
  }

  return clipped;
}

export default SplitPolygonMode;
